import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Building2, User, Check, Gift } from "lucide-react";
import { AuthShell } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { local, errMsg } from "@/lib/api";

const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

const ORG_TYPES = [
  { value: "hospital", label: "Hospital / Clinic" },
  { value: "external_lab", label: "Diagnostic Laboratory" },
  { value: "external_radiology", label: "Radiology / Imaging Centre" },
];

export default function Signup() {
  const { registerOrg } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", password: "",
    org_name: "", org_type: "hospital", phone: "", locality: "", agreement_accepted: false,
    promo: (params.get("promo") || "").toUpperCase(),
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const [promoInfo, setPromoInfo] = useState(null);
  useEffect(() => {
    const c = form.promo.trim();
    if (!c) { setPromoInfo(null); return undefined; }
    const t = setTimeout(() => {
      local.get(`/trial-promos/check?code=${encodeURIComponent(c)}`).then((r) => setPromoInfo(r.data)).catch(() => setPromoInfo(null));
    }, 350);
    return () => clearTimeout(t);
  }, [form.promo]);

  const [avail, setAvail] = useState(null);
  useEffect(() => {
    const s = slugify(form.org_name), l = slugify(form.locality) || "main";
    if (!s) { setAvail(null); return; }
    const t = setTimeout(() => {
      local.get(`/brand-available?slug=${s}&locality=${l}`).then((r) => setAvail(r.data.available)).catch(() => setAvail(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.org_name, form.locality]);

  const next = (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || form.password.length < 6) {
      toast.error("Enter your name, email and a password (min 6 chars)");
      return;
    }
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agreement_accepted) { toast.error("Please accept the Terms & Privacy Policy"); return; }
    if (avail === false) { toast.error("That branded login link is taken — change the area/locality"); return; }
    setLoading(true);
    try {
      const res = await registerOrg({
        org_name: form.org_name, org_type: form.org_type, email: form.email.trim(),
        password: form.password, full_name: form.full_name, phone: form.phone || null,
        agreement_accepted: true,
      });
      toast.success(`${res.user?.full_name || "Welcome"}! ${form.org_name} is ready.`);
      const slug = slugify(form.org_name);
      const locality = slugify(form.locality) || "main";
      try {
        await local.post("/org-profile", { slug, locality });
        localStorage.setItem("fhapas_brand_path", `${slug}/${locality}`);
      } catch (_) {}
      if (promoInfo?.valid) {
        try {
          const pr = (await local.post("/trial-promos/redeem", { code: promoInfo.code })).data;
          toast.success(`${pr.trial_days}-day extended free trial activated with ${pr.code}`);
        } catch (err) {
          toast.error(errMsg(err, "Promo code could not be applied"));
        }
      }
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(errMsg(err, "Could not register organisation"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your organisation"
      subtitle="Set up FHAPAS for your hospital, lab or imaging centre"
      image="https://images.unsplash.com/photo-1580281657702-257584239a55?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
      footer={<>Already registered? <Link to="/login" data-testid="link-login" className="font-semibold text-teal-700 hover:underline">Sign in</Link></>}
    >
      <div className="mb-6 flex items-center gap-2">
        {[1, 2].map((s) => (
          <div key={s} className="flex flex-1 items-center gap-2">
            <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${step >= s ? "bg-teal-700 text-white" : "bg-slate-200 text-slate-500"}`}>
              {step > s ? <Check className="h-4 w-4" /> : s}
            </span>
            <span className={`text-xs font-medium ${step >= s ? "text-slate-800" : "text-slate-400"}`}>{s === 1 ? "Admin account" : "Organisation"}</span>
            {s === 1 && <div className={`h-0.5 flex-1 ${step > 1 ? "bg-teal-700" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      {step === 1 ? (
        <form onSubmit={next} className="space-y-4">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700"><User className="h-4 w-4 text-teal-700" /> Administrator</div>
          <div className="space-y-1.5"><Label>Full name</Label>
            <Input data-testid="signup-fullname" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required placeholder="Dr. Amaka Okafor" /></div>
          <div className="space-y-1.5"><Label>Work email</Label>
            <Input data-testid="signup-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required placeholder="admin@hospital.com" /></div>
          <div className="space-y-1.5"><Label>Password</Label>
            <PasswordInput data-testid="signup-password" value={form.password} onChange={(e) => set("password", e.target.value)} required placeholder="At least 6 characters" /></div>
          <Button data-testid="signup-next" type="submit" className="w-full bg-teal-700 hover:bg-teal-800">Continue</Button>
        </form>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700"><Building2 className="h-4 w-4 text-teal-700" /> Organisation details</div>
          <div className="space-y-1.5"><Label>Organisation name</Label>
            <Input data-testid="signup-orgname" value={form.org_name} onChange={(e) => set("org_name", e.target.value)} required placeholder="Fendana General Hospital" /></div>
          <div className="space-y-1.5"><Label>Organisation type</Label>
            <Select value={form.org_type} onValueChange={(v) => set("org_type", v)}>
              <SelectTrigger data-testid="signup-orgtype"><SelectValue /></SelectTrigger>
              <SelectContent>{ORG_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Phone (optional)</Label>
            <Input data-testid="signup-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+234 800 000 0000" /></div>
          <div className="space-y-1.5"><Label>Area / locality</Label>
            <Input data-testid="signup-locality" value={form.locality} onChange={(e) => set("locality", e.target.value)} placeholder="e.g. Allen Avenue" />
            {form.org_name && <p className="text-xs text-slate-400">Your branded login: <span className="font-mono text-teal-700">/{slugify(form.org_name)}/{slugify(form.locality) || "main"}</span>{avail === false && <span className="ml-2 font-semibold text-rose-600" data-testid="signup-slug-taken">· taken, change locality</span>}{avail === true && <span className="ml-2 font-semibold text-emerald-600">· available</span>}</p>}
          </div>
          <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><Gift className="h-3.5 w-3.5 text-amber-600" /> Promo / event code (optional)</Label>
            <Input data-testid="signup-promo" value={form.promo} onChange={(e) => set("promo", e.target.value.toUpperCase())} placeholder="e.g. NMA2026" className="font-mono uppercase" />
            {promoInfo?.valid && <p className="text-xs font-semibold text-emerald-600" data-testid="signup-promo-valid">{promoInfo.trial_days}-day extended free trial will be applied</p>}
            {promoInfo && !promoInfo.valid && <p className="text-xs font-semibold text-rose-600" data-testid="signup-promo-invalid">This code is not valid</p>}
          </div>
          <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 p-3 text-sm text-slate-600">
            <Checkbox data-testid="signup-agreement" checked={form.agreement_accepted} onCheckedChange={(v) => set("agreement_accepted", !!v)} className="mt-0.5" />
            <span>I accept the <Link to="/terms" target="_blank" className="font-medium text-teal-700 hover:underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="font-medium text-teal-700 hover:underline">Privacy Policy</Link>.</span>
          </label>
          <div className="flex gap-3">
            <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(1)} data-testid="signup-back">Back</Button>
            <Button data-testid="signup-submit" type="submit" disabled={loading} className="flex-1 bg-teal-700 hover:bg-teal-800">
              {loading ? "Creating…" : "Create organisation"}
            </Button>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
